import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ProfileImageCropper } from "./ProfileImageCropper";
import { SettingsDialogForm } from "./settings-dialog/SettingsDialogForm";
import { useSettingsDialog } from "./settings-dialog/useSettingsDialog";
import { useToast } from "@/hooks/use-toast";

interface SettingsDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  businessName: string;
  profileImage: string | null;
  isGeneratingName: boolean;
  onSubmit: (phonePrefix: string, phoneNumber: string | null, businessName: string) => Promise<void>;
  onBusinessNameChange: (value: string) => void;
  onGenerateBusinessName: () => Promise<void>;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteImage?: () => void;
  country?: string;
}

export const SettingsDialog = ({
  isOpen,
  isLoading,
  businessName,
  profileImage,
  isGeneratingName,
  onSubmit,
  onBusinessNameChange,
  onGenerateBusinessName,
  onImageUpload,
  onDeleteImage,
  country = "Kenya",
}: SettingsDialogProps) => {
  const { toast } = useToast();
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  
  const {
    fileInputRef,
    selectedPrefix,
    phoneNumber,
    preferredMessaging,
    currency,
    businessNameError,
    handleSubmit,
    handleBusinessNameChange,
    handleFileInputClick,
    setSelectedPrefix,
    setPhoneNumber,
    setPreferredMessaging,
    setCurrency,
  } = useSettingsDialog({
    businessName,
    country,
    onBusinessNameChange,
    onGenerateBusinessName,
    onImageUpload,
    onSubmit
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempImageUrl(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    // Convert the data URL to a File object
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
      
      // Create a synthetic event to match what onImageUpload expects
      const syntheticEvent = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      // Pass the synthetic event to the parent's onImageUpload function
      onImageUpload(syntheticEvent);
      
      // Close the cropper and clear the temp image
      setShowCropper(false);
      setTempImageUrl(null);
    } catch (error) {
      console.error("Error processing cropped image:", error);
    }
  };

  const handleDeleteImage = () => {
    if (onDeleteImage) {
      onDeleteImage();
      toast({
        title: "Success",
        description: "Profile image removed successfully",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        // If the user tries to close the dialog, handle auth cleanup and redirect
        try {
          // Just redirect back to home
          window.location.href = '/';
        } catch (error) {
          console.error('Error navigating from settings dialog:', error);
        }
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-3xl font-bold text-gebeya-pink">
            Customize Your Service Page
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            Set up your profile and communication preferences
          </DialogDescription>
        </DialogHeader>

        <SettingsDialogForm
          isLoading={isLoading}
          businessName={businessName}
          businessNameError={businessNameError}
          profileImage={profileImage}
          isGeneratingName={isGeneratingName}
          selectedPrefix={selectedPrefix}
          phoneNumber={phoneNumber}
          preferredMessaging={preferredMessaging}
          currency={currency}
          fileInputRef={fileInputRef}
          onSubmit={handleSubmit}
          onBusinessNameChange={handleBusinessNameChange}
          onGenerateBusinessName={onGenerateBusinessName}
          onFileInputClick={handleFileInputClick}
          onPrefixChange={setSelectedPrefix}
          onPhoneNumberChange={setPhoneNumber}
          onPreferredMessagingChange={setPreferredMessaging}
          onCurrencyChange={setCurrency}
          onImageUpload={handleFileChange}
          onDeleteImage={handleDeleteImage}
        />

        {showCropper && tempImageUrl && (
          <ProfileImageCropper
            open={showCropper}
            onClose={() => {
              setShowCropper(false);
              setTempImageUrl(null);
            }}
            imageUrl={tempImageUrl}
            onCropComplete={handleCropComplete}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
